import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import {
  Conversation,
  windowRemainingMs,
  windowUrgency
} from '../../../types/models/whatsapp';

interface WindowCountdownProps {
  conversation: Conversation;
}

const formatRemaining = (ms: number): string => {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

const URGENCY_STYLES: Record<string, string> = {
  open: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  critical: 'bg-danger-50 text-danger-600',
  closed: 'bg-gray-100 text-gray-500'
};

/**
 * Ticks once a minute rather than once a second — the value is displayed in
 * minutes, so a faster interval would re-render the thread for no visible gain.
 */
const WindowCountdown: React.FC<WindowCountdownProps> = ({ conversation }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const remaining = windowRemainingMs(conversation, now);
  const urgency = windowUrgency(conversation, now);

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${URGENCY_STYLES[urgency]}`}
    >
      <Clock className="h-3 w-3" />
      {urgency === 'closed' ? '24h window closed' : formatRemaining(remaining)}
    </span>
  );
};

export default WindowCountdown;
